import {isNetworkError} from './connectivity'

/**
 * Retry policy for the onboarding tracking POSTs (onboarding/track-events and
 * timetable/track-event) — see onboardingEventTracking.ts for the callers.
 *
 * Tracking is fire-and-forget, so a failed POST used to be a silently lost funnel row. The
 * usual cause is the API's rate limiter (429 Too Many Requests) when rows are posted in a
 * burst: one Next click can dispatch a batch, a reach row and a preferences PUT in the same
 * tick. Those failures are worth waiting out — the row is just as true a minute later.
 *
 * Retried: 429 (by status code or a "Too many requests" body), a non-JSON error page (the
 * saga's JSON parse throws on the rate limiter's or a gateway's text response, so the failure
 * arrives as a SyntaxError) and network failures. Not retried: anything else (400 validation,
 * 401, 403, 500) — repeating those cannot help.
 *
 * Backoff: doubles 1s → 8s, then +2s per attempt (10s, 12s, 14s, …), capped at 30s, ±20%
 * jitter, at most TRACKING_MAX_ATTEMPTS attempts (~1.5 minutes in total). Tracking never
 * blocks navigation, so the wait costs the user nothing, but a permanently rate-limited
 * endpoint must not be hammered for the life of the tab, hence the cap.
 */
export const TRACKING_MAX_ATTEMPTS = 10
export const TRACKING_RETRY_BASE_MS = 1000
/** Last delay of the doubling phase; after it the delay grows linearly. Must be BASE × 2^n. */
export const TRACKING_RETRY_LINEAR_FROM_MS = 8000
export const TRACKING_RETRY_LINEAR_STEP_MS = 2000
export const TRACKING_RETRY_MAX_DELAY_MS = 30_000

/** One attempt: dispatches the request and reports the outcome through the two callbacks. */
export type TrackingSender = (onSuccess: () => void, onFailure: (failure: unknown) => void) => void

/**
 * Whether a tracking failure is transient. `failure` is whatever the generic saga handed to
 * `onFailure`: the parsed JSON error body (`{statusCode, message, …}`), or the thrown error
 * when the request or the JSON parse failed.
 */
export const isRetryableTrackingFailure = (failure: unknown): boolean => {
  if (!failure) return false
  const f = failure as Record<string, any>
  const status = Number(f.statusCode ?? f.status ?? f.response?.status ?? NaN)
  if (status === 429) return true
  const text = `${f.message ?? ''} ${f.error ?? ''}`.toLowerCase()
  if (text.includes('too many request')) return true
  // The saga tried to `.json()` a text/html error page (rate limiter, gateway).
  if (failure instanceof SyntaxError) return true
  return isNetworkError(failure)
}

/**
 * Delay before retry number `attempt + 1`, i.e. after the `attempt`-th failure (1-based).
 * Doubles up to TRACKING_RETRY_LINEAR_FROM_MS, then grows by TRACKING_RETRY_LINEAR_STEP_MS per
 * attempt: 1s, 2s, 4s, 8s, 10s, 12s, 14s, …
 */
export const trackingRetryDelayMs = (attempt: number, random: () => number = Math.random) => {
  const step = Math.max(0, attempt - 1)
  const exponential = TRACKING_RETRY_BASE_MS * 2 ** step
  const linearSteps = step - Math.log2(TRACKING_RETRY_LINEAR_FROM_MS / TRACKING_RETRY_BASE_MS)
  const base = Math.min(
    TRACKING_RETRY_MAX_DELAY_MS,
    exponential <= TRACKING_RETRY_LINEAR_FROM_MS
      ? exponential
      : TRACKING_RETRY_LINEAR_FROM_MS + TRACKING_RETRY_LINEAR_STEP_MS * linearSteps
  )
  return Math.round(base * (0.8 + random() * 0.4))
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

/**
 * Runs `send` until it succeeds, retrying transient failures per the policy above. Always
 * resolves — on success, on a non-retryable failure, or once the attempts are exhausted —
 * because tracking must never block or break the caller. `wait` is injectable for tests.
 */
export const sendWithRetry = (
  send: TrackingSender,
  wait: (ms: number) => Promise<void> = sleep
): Promise<void> => {
  const attempt = (n: number): Promise<void> =>
    new Promise<{ok: boolean; failure?: unknown}>((resolve) => {
      try {
        send(
          () => resolve({ok: true}),
          (failure) => resolve({ok: false, failure})
        )
      } catch (failure) {
        resolve({ok: false, failure})
      }
    }).then(({ok, failure}) => {
      if (ok) return undefined
      if (n >= TRACKING_MAX_ATTEMPTS || !isRetryableTrackingFailure(failure)) return undefined
      return wait(trackingRetryDelayMs(n)).then(() => attempt(n + 1))
    })
  return attempt(1)
}
